@file:OptIn(ExperimentalBlockRichTextApi::class)

package com.taocent.simple.compose.component.blockrichtext

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue

/**
 * BlockState 块合并、删除、左高亮 Backspace 拦截相关的扩展函数。
 *
 * - [tryDeleteAtBlockStart] 处理块起始位置的 Backspace,触发左高亮选中
 * - [tryHandleTextBlockValueChangeBeforeApply] 在 IME 输入前预判 Backspace
 * - [deleteBlock] 直接删除指定块
 * - [mergeWithPreviousBlock] 合并到上一个文本块
 */

fun BlockState.tryDeleteAtBlockStart(blockId: String): Boolean {
    if (backspaceBlockDeleteGuard) {
        bumpGuardCooldown()
        return true
    }
    val index = findBlockIndex(blockId)
    if (index <= 0) return false
    val previousBlock = _blocks[index - 1]
    if (previousBlock is TableBlock) {
        if (selectedTableBlockId == previousBlock.id) {
            val deleted = deleteSelectedTable()
            if (deleted) {
                backspaceBlockDeleteGuard = true
                bumpGuardCooldown()
                println("[BS_GUARD] SET true (tryDeleteAtBlockStart: deleted table=${previousBlock.id})")
            }
            return deleted
        }
        selectedTableBlockId = previousBlock.id
        return true
    }
    return false
}

fun BlockState.tryHandleTextBlockValueChangeBeforeApply(
    blockId: String,
    oldValue: TextFieldValue,
    newValue: TextFieldValue
): Boolean {
    if (backspaceBlockDeleteGuard) {
        println("[BS_GUARD] REVERT onChange (guard=true)")
        bumpGuardCooldown()
        return true
    }
    if (oldValue.text.isNotEmpty()) return false
    if (newValue.text.isNotEmpty()) return false
    if (!oldValue.selection.collapsed || oldValue.selection.min != 0) return false
    if (!newValue.selection.collapsed || newValue.selection.min != 0) return false
    return tryDeleteAtBlockStart(blockId)
}

fun BlockState.deleteBlock(blockId: String) {
    if (_blocks.size <= 1) return
    val index = findBlockIndex(blockId)
    if (index < 0) return
    val before = documentSnapshot()
    _blocks.removeAt(index)
    blockStates.remove(blockId)
    if (focusedBlockId == blockId) {
        val newFocusIndex = if (index > 0) index - 1 else 0
        setFocusedBlockCompat(_blocks[newFocusIndex].id)
    }
    recordDocumentChange(before)
}

fun BlockState.mergeWithPreviousBlock(blockId: String) {
    val index = findBlockIndex(blockId)
    if (index <= 0) return
    val previousBlock = _blocks[index - 1]
    val currentBlock = _blocks[index]
    if (previousBlock !is TextBlock || currentBlock !is TextBlock) return
    val before = documentSnapshot()
    val previousState = getBlockState(previousBlock.id)
    val currentState = getBlockState(currentBlock.id)
    val previousContent = previousState.textFieldValue.annotatedString
    val currentContent = currentState.textFieldValue.annotatedString
    val mergedBuilder = AnnotatedString.Builder(previousContent.length + currentContent.length)
    mergedBuilder.append(previousContent)
    mergedBuilder.append(currentContent)
    val merged = mergedBuilder.toAnnotatedString()
    previousState.restoreTextFieldValue(
        TextFieldValue(
            annotatedString = merged,
            selection = TextRange(previousContent.length)
        )
    )
    syncBlockContent(previousBlock.id)
    _blocks.removeAt(index)
    blockStates.remove(currentBlock.id)
    setFocusedBlockCompat(previousBlock.id)
    recordDocumentChange(before)
}
